import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { History, PanelRight } from 'lucide-react'
import { toast } from 'sonner'
import { useAgents } from '@/contexts/AgentsContext'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { apiActivateTemplate, apiGetTemplate, isApiError, type FormTemplate } from '@/lib/api'
import {
  diffTemplates,
  generatedTemplateFromFormTemplate,
  selectDiffBase,
  selectTemplateSnapshots,
  type ManualOverride,
  type TemplateSnapshot,
} from '@/lib/templatePreview'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsMobile } from '@/components/ui/use-mobile'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ChatContainer } from '@/components/agents/ChatContainer'
import { ConversationList } from '@/components/agents/ConversationList'
import { SplitView, type SplitViewHandle } from '@/components/agents/SplitView'
import { TemplatePreview, type TemplatePreviewStatus } from '@/components/agents/TemplatePreview'
import { WelcomeMessage } from '@/components/agents/WelcomeMessage'
import { TemplateEditor } from '@/components/TemplateEditor'

const headerButtonClass =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2'

type KnownTemplateStatus = 'draft' | 'active' | 'missing'

function isNotFound(e: unknown): boolean {
  return isApiError(e) && e.status === 404
}

function statusFromRow(t: FormTemplate): KnownTemplateStatus {
  return t.status === 'draft' ? 'draft' : 'active'
}

// Vue Agents pleine page : historique (colonne md+, Sheet en mobile) + chat.
// Story 6.3 : Split View chat | TemplatePreview dès qu'un template est généré
// (dérivé des messages, live comme au rechargement) ; Sheet en mobile.
// Story 6.4 : corrections (✨ par lignée de templateId), « Utiliser ce
// template », « Modifier manuellement » (éditeur dans un Sheet), réouverture
// de la preview repliée par drag.
export function ChatView() {
  const { user } = useAuth()
  const {
    conversations,
    conversationsLoading,
    conversationsError,
    activeConversationId,
    messages,
    isStreaming,
    selectConversation,
    startNewConversation,
  } = useAgents()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [templateSheetOpen, setTemplateSheetOpen] = useState(false)
  // Snapshot fermé par l'utilisateur ; une nouvelle génération (autre id) rouvre la preview.
  const [dismissedInvocationId, setDismissedInvocationId] = useState<string | null>(null)
  // Gestion du focus : 'button' après fermeture, 'preview' après réouverture via le bouton.
  const [focusPreview, setFocusPreview] = useState(false)
  const [focusRequest, setFocusRequest] = useState(0)
  // Story 6.4 — statut serveur par templateId, action en cours, éditeur, versions manuelles.
  const [templateStatus, setTemplateStatus] = useState<Record<string, KnownTemplateStatus>>({})
  const [actionPending, setActionPending] = useState<'use' | 'edit' | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null)
  const [manualOverrides, setManualOverrides] = useState<Record<string, ManualOverride>>({})
  const [previewCollapsed, setPreviewCollapsed] = useState(false)
  const splitRef = useRef<SplitViewHandle>(null)
  const showTemplateRef = useRef<HTMLButtonElement>(null)
  const focusButtonNext = useRef(false)
  const isMobile = useIsMobile()
  const isLg = useMediaQuery('(min-width: 1024px)')

  const snapshots = useMemo(() => selectTemplateSnapshots(messages), [messages])
  const latest = snapshots[snapshots.length - 1] as TemplateSnapshot | undefined
  const latestInvocationId = latest?.invocationId ?? null
  const latestTemplateId = latest?.template.templateId ?? null

  // Version affichée : la version manuelle enregistrée sur ce snapshot (sans ✨),
  // sinon le snapshot avec le diff de lignée (même templateId uniquement).
  const override = latestTemplateId ? manualOverrides[latestTemplateId] : undefined
  const manuallyEdited = override !== undefined && override.baseInvocationId === latestInvocationId
  const displayedTemplate = manuallyEdited ? override.template : latest?.template
  const diff = useMemo(() => {
    if (!latest || manuallyEdited) return null
    const base = selectDiffBase(snapshots, snapshots.length - 1, manualOverrides)
    return base ? diffTemplates(base, latest.template) : null
  }, [latest, manuallyEdited, snapshots, manualOverrides])

  const previewOpen = latest !== undefined && latest.invocationId !== dismissedInvocationId
  const splitActive = !isMobile && previewOpen
  const showTemplateButton =
    latest !== undefined && (isMobile || !previewOpen || (splitActive && previewCollapsed))
  const status: TemplatePreviewStatus = latestTemplateId
    ? templateStatus[latestTemplateId] ?? 'loading'
    : 'loading'

  // Statut serveur du template affiché : uniquement quand l'id change (pas à chaque message).
  useEffect(() => {
    if (!latestTemplateId) return
    let ignore = false
    void (async () => {
      try {
        const t = await apiGetTemplate(latestTemplateId)
        if (!ignore) setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: statusFromRow(t) }))
      } catch (e: unknown) {
        if (ignore) return
        if (!isNotFound(e)) console.error('[ChatView] statut du template', e)
        setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: isNotFound(e) ? 'missing' : 'draft' }))
      }
    })()
    return () => {
      ignore = true
    }
  }, [latestTemplateId])

  // Nouvelle conversation, ou Split View démonté (preview fermée, mobile) : le
  // prochain SplitView repart à 50/50, l'état replié ne vaut plus.
  useEffect(() => {
    setPreviewCollapsed(false)
  }, [activeConversationId, splitActive])

  // Nouvelle version alors que la preview est repliée par drag : on la rouvre.
  const collapsedRef = useRef(previewCollapsed)
  collapsedRef.current = previewCollapsed
  useEffect(() => {
    if (latestInvocationId && collapsedRef.current) splitRef.current?.expandRight()
  }, [latestInvocationId])

  const handleSelect = useCallback(
    (id: string) => {
      setHistoryOpen(false)
      setDismissedInvocationId(null)
      void selectConversation(id)
    },
    [selectConversation]
  )

  const handleNew = useCallback(() => {
    setHistoryOpen(false)
    setDismissedInvocationId(null)
    startNewConversation()
  }, [startNewConversation])

  const handleClosePreview = useCallback(() => {
    focusButtonNext.current = true
    setFocusPreview(false)
    setDismissedInvocationId(latestInvocationId)
  }, [latestInvocationId])

  useEffect(() => {
    if (focusButtonNext.current && showTemplateButton) {
      focusButtonNext.current = false
      showTemplateRef.current?.focus()
    }
  }, [showTemplateButton])

  const handleShowTemplate = useCallback(() => {
    if (isMobile) {
      setTemplateSheetOpen(true)
      return
    }
    setFocusPreview(true)
    setFocusRequest((n) => n + 1)
    if (previewOpen && previewCollapsed) splitRef.current?.expandRight()
    else setDismissedInvocationId(null)
  }, [isMobile, previewOpen, previewCollapsed])

  const handleUse = useCallback(async () => {
    if (!latestTemplateId) return
    setActionPending('use')
    try {
      const t = await apiActivateTemplate(latestTemplateId)
      setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: statusFromRow(t) }))
      toast.success('Template ajouté à votre bibliothèque')
    } catch (e: unknown) {
      if (isNotFound(e)) setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: 'missing' }))
      toast.error("Impossible d'ajouter le template à la bibliothèque")
    } finally {
      setActionPending(null)
    }
  }, [latestTemplateId])

  const handleEditManually = useCallback(async () => {
    if (!latestTemplateId) return
    setActionPending('edit')
    try {
      const t = await apiGetTemplate(latestTemplateId)
      setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: statusFromRow(t) }))
      setEditingTemplate(t)
    } catch (e: unknown) {
      if (isNotFound(e)) setTemplateStatus((prev) => ({ ...prev, [latestTemplateId]: 'missing' }))
      toast.error("Impossible d'ouvrir le template")
    } finally {
      setActionPending(null)
    }
  }, [latestTemplateId])

  const closeEditor = useCallback(() => setEditingTemplate(null), [])

  const editingId = editingTemplate?.id ?? null
  const handleEditorSaved = useCallback(async () => {
    const baseInvocationId = latestInvocationId
    if (editingId && baseInvocationId) {
      try {
        const t = await apiGetTemplate(editingId)
        const template = generatedTemplateFromFormTemplate(t)
        setTemplateStatus((prev) => ({ ...prev, [editingId]: statusFromRow(t) }))
        if (template) {
          setManualOverrides((prev) => ({ ...prev, [editingId]: { baseInvocationId, template } }))
        }
      } catch (e: unknown) {
        console.error('[ChatView] relecture du template après édition', e)
        toast.error("Template enregistré, mais impossible d'actualiser l'aperçu. Rechargez la conversation.")
      }
    }
    setEditingTemplate(null)
  }, [editingId, latestInvocationId])

  const handleEditorOpenChange = useCallback((open: boolean) => {
    if (!open) setEditingTemplate(null)
  }, [])

  const list = (
    <ConversationList
      conversations={conversations}
      loading={conversationsLoading}
      error={conversationsError}
      activeId={activeConversationId}
      onSelect={handleSelect}
      onNew={handleNew}
    />
  )

  const chat = (
    <ChatContainer
      className="flex-1"
      welcome={user ? <WelcomeMessage name={user.name} email={user.email} /> : null}
    />
  )

  const previewActions = {
    status,
    onUse: handleUse,
    onEditManually: handleEditManually,
    actionPending,
    manuallyEdited,
    // Décision PO Q9 : pas d'édition manuelle pendant qu'une correction peut arriver.
    editDisabled: isStreaming,
  }

  return (
    <section aria-label="Agents" className="flex h-full min-h-0 flex-1 bg-background">
      <aside
        className={cn(
          'hidden w-[260px] shrink-0 border-r bg-slate-50/60 md:flex md:flex-col',
          // Split View : colonne historique masquée sous xl (accessible via le bouton « Historique »).
          splitActive && 'md:hidden xl:flex'
        )}
      >
        {list}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={cn(
            'flex items-center justify-between border-b px-4 py-2 md:hidden',
            splitActive && 'md:flex xl:hidden',
            showTemplateButton && 'md:flex'
          )}
        >
          <span className="text-sm font-semibold text-slate-800">Agents</span>
          <div className="flex items-center gap-1">
            {showTemplateButton && (
              <button ref={showTemplateRef} type="button" onClick={handleShowTemplate} className={headerButtonClass}>
                <PanelRight aria-hidden="true" className="h-4 w-4" />
                Voir le template
              </button>
            )}
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className={cn(headerButtonClass, 'md:hidden', splitActive && 'md:inline-flex xl:hidden')}
            >
              <History aria-hidden="true" className="h-4 w-4" />
              Historique
            </button>
          </div>
        </div>
        {splitActive && latest && displayedTemplate ? (
          <SplitView
            ref={splitRef}
            className="min-h-0 flex-1"
            direction={isLg ? 'horizontal' : 'vertical'}
            onRightCollapsedChange={setPreviewCollapsed}
            left={chat}
            right={
              <TemplatePreview
                template={displayedTemplate}
                diff={diff}
                snapshotId={manuallyEdited ? `${latest.invocationId}:manual` : latest.invocationId}
                onClose={handleClosePreview}
                focusOnMount={focusPreview}
                focusRequest={focusRequest}
                {...previewActions}
              />
            }
          />
        ) : (
          chat
        )}
      </div>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-[320px] gap-0 p-0">
          <SheetHeader className="border-b">
            <SheetTitle>Historique</SheetTitle>
            <SheetDescription className="sr-only">Vos conversations avec l'agent</SheetDescription>
          </SheetHeader>
          {list}
        </SheetContent>
      </Sheet>

      <Sheet open={isMobile && templateSheetOpen && latest !== undefined} onOpenChange={setTemplateSheetOpen}>
        <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-xl">
          <SheetHeader className="border-b">
            <SheetTitle>Template</SheetTitle>
            <SheetDescription className="sr-only">Prévisualisation du template généré par l'agent</SheetDescription>
          </SheetHeader>
          {latest && displayedTemplate && (
            <TemplatePreview
              className="min-h-0 flex-1"
              template={displayedTemplate}
              diff={diff}
              snapshotId={manuallyEdited ? `${latest.invocationId}:manual` : latest.invocationId}
              {...previewActions}
            />
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={editingTemplate !== null} onOpenChange={handleEditorOpenChange}>
        <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-3xl">
          <SheetHeader className="border-b">
            <SheetTitle>Modifier le template</SheetTitle>
            <SheetDescription className="sr-only">Édition manuelle du template généré par l'agent</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-6">
            {editingTemplate && (
              <TemplateEditor
                key={editingTemplate.id}
                template={editingTemplate}
                onSave={handleEditorSaved}
                onCancel={closeEditor}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  )
}
